import { OutlinedInput, Stack } from '@mui/material';
import {
  type ChangeEvent,
  type ClipboardEvent,
  type FC,
  type KeyboardEvent,
  useState,
} from 'react';

type Props = {
  value: string;
  onChange: (code: string) => void;
  length?: number;
  autoFocus?: boolean;
  isError?: boolean;
  name?: string;
};

export const CodeInput: FC<Props> = ({
  value,
  onChange,
  length = 6,
  autoFocus = true,
  isError = false,
  name = 'code',
}) => {
  const toDigits = (code: string) => Array.from({ length }, (_, index) => code[index] ?? '');

  // Kept locally so clearing a middle box leaves a hole instead of shifting the next digits left.
  const [digits, setDigits] = useState(() => toDigits(value));
  if (toDigits(value).join('') !== digits.join('')) setDigits(toDigits(value));

  const focusField = (index: number) =>
    document.querySelector<HTMLElement>(`input[name='${name}.${index}']`)?.focus();

  const updateDigits = (nextDigits: string[]) => {
    setDigits(nextDigits);
    onChange(nextDigits.join(''));
  };

  const fillFrom = (index: number, text: string) => {
    const chars = text.replace(/\s/g, '').slice(0, length - index);
    if (!chars) return;

    const nextDigits = [...digits];
    nextDigits.splice(index, chars.length, ...chars);
    updateDigits(nextDigits);
    focusField(Math.min(index + chars.length, length - 1));
  };

  const clearDigit = (index: number) =>
    updateDigits(digits.map((digit, digitIndex) => (digitIndex === index ? '' : digit)));

  const handleChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const { value: input } = event.target;
    // A filled box still holds its previous digit: drop it so typing over it replaces it.
    const typed = input.length > 1 ? input.replace(digits[index], '') : input;

    if (typed) fillFrom(index, typed);
    else clearDigit(index);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>, index: number) => {
    event.preventDefault();
    fillFrom(index, event.clipboardData.getData('text'));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key !== 'Backspace') return;
    event.preventDefault();

    const target = digits[index] || index === 0 ? index : index - 1;
    clearDigit(target);
    focusField(target);
  };

  return (
    <Stack direction="row" spacing={1} justifyContent="center">
      {digits.map((digit, index) => (
        <OutlinedInput
          // biome-ignore lint/suspicious/noArrayIndexKey: positional boxes, no stable id
          key={index}
          name={`${name}.${index}`}
          value={digit}
          error={isError}
          autoFocus={autoFocus && index === 0}
          placeholder="-"
          onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange(event, index)}
          onPaste={(event: ClipboardEvent<HTMLInputElement>) => handlePaste(event, index)}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => handleKeyDown(event, index)}
          sx={{ width: { xs: 36, sm: 44 }, height: { xs: 48, sm: 56 } }}
          inputProps={{
            className: 'field-code',
            sx: { p: 0, textAlign: 'center', typography: 'h4' },
          }}
        />
      ))}
    </Stack>
  );
};
